<template>
    <div
        v-if="(isVisible || isAnimating) && !isExport"
        ref="floatingButtonRef"
        :class="[
            $style.support_floating_icon,
            {
                [$style.support_floating_icon_new_message]: hasNewMessage,
                [$style.support_floating_icon_animating]: isAnimating,
            },
        ]"
        :style="animationStyle"
        @click="handleClick"
        @mouseenter="isMainButtonHovered = true"
        @mouseleave="isMainButtonHovered = false"
    >
        <div :class="$style.support_floating_icon_circle">
            <img :src="chatIconSrc" alt="Support chat" :class="$style.support_floating_icon_image" />
        </div>
        <div v-if="hasNewMessage" :class="$style.support_floating_icon_hotpoint" />
        <button
            v-if="isMainButtonHovered && !isAnimating"
            :class="[
                $style.support_floating_icon_close,
                { [$style.support_floating_icon_close_hovered]: isCloseButtonHovered },
            ]"
            @click.stop="handleClose"
            @mouseenter="isCloseButtonHovered = true"
            @mouseleave="isCloseButtonHovered = false"
        >
            <svg-sprite name="close-2" type="bold" :size="24" :style="{ color: closeIconColor }" />
        </button>
    </div>
</template>

<script>
import app, { appIds } from "$$app";
import projectsModel from "$$projectModel";
import SvgSprite from "$$vueCmp/VueIcon/svgSprite.vue";

const ID_APP_EVENT_SUPPORTCHAT_SHOW = "app:livechat:show";
const ID_APP_EVENT_SUPPORTCHAT_HIDE = "app:livechat:hide";
const ID_KEY_STORAGE_CHAT_MISSING = "liveChatMissing";

export default {
    name: "LearningCenterSupportFloatingIcon",
    components: {
        SvgSprite,
    },
    data() {
        return {
            locales: __,
            isVisible: false,
            hasNewMessage: false,
            isChatMinimized: false,
            isCloseButtonHovered: false,
            isMainButtonHovered: false,
            isAnimating: false,
            animationStyle: {},
        };
    },
    computed: {
        closeIconColor() {
            if (this.isCloseButtonHovered) {
                return "#808080";
            }
            return this.isMainButtonHovered ? "#999" : "#FFFFFF";
        },
        chatIconSrc() {
            return `${GT.cdn}/imgs/learnCenter/chats.svg`;
        },
        isActivitiesLoaded() {
            return this.$store.state.user.isActivitiesLoaded;
        },
        isFinishedOnboarding() {
            const onboardingActivitySettings = this.$store.getters["user/getActivitySettings"]("onboarding");
            return onboardingActivitySettings?.isFinished;
        },
        isExport() {
            return  this.$store.getters.isExport;
        }
    },

    watch: {
        isActivitiesLoaded: "checkInitialState",
        isFinishedOnboarding: "checkInitialState",
        "$route.name": function (newName, oldName) {
            if (oldName === "onboarding" && newName !== "onboarding") {
                this.checkInitialState();
            }
        },
    },

    mounted() {
        this.setupListeners();
        this.checkInitialState();
    },
    beforeUnmount() {
        this.removeListeners();
    },
    methods: {
        setupListeners() {
            app.on(ID_APP_EVENT_SUPPORTCHAT_SHOW, this.handleChatShow);
            app.on(ID_APP_EVENT_SUPPORTCHAT_HIDE, this.handleChatHide);
            app.on("app:livechat:new_message", this.handleNewMessage);
            app.on("created:first:project", this.checkInitialState);
            app.on("init:projects", this.checkInitialState);
        },
        removeListeners() {
            app.off(ID_APP_EVENT_SUPPORTCHAT_SHOW, this.handleChatShow);
            app.off(ID_APP_EVENT_SUPPORTCHAT_HIDE, this.handleChatHide);
            app.off("app:livechat:new_message", this.handleNewMessage);
            app.off("created:first:project", this.checkInitialState);
            app.off("init:projects", this.checkInitialState);
        },
        checkInitialState() {
            this.$nextTick(() => {
                // Никогда не показываем кнопку на роуте онбординга
                const isOnboardingRoute = this.$route && this.$route.name === "onboarding";
                if (isOnboardingRoute) {
                    this.isVisible = false;
                    return;
                }

                if (!this.isActivitiesLoaded) return;
                if (this.isExport) return;

                const isClosedByUser = webix.storage.local.get(ID_KEY_STORAGE_CHAT_MISSING);
                if (isClosedByUser) {
                    this.isVisible = false;
                    return;
                }

                const projectsCount = projectsModel.getAllProjects().length;
                const finishedOnboarding = !!this.isFinishedOnboarding;

                if (finishedOnboarding && projectsCount >= 1) {
                    this.isVisible = true;
                } else {
                    this.isVisible = false;
                }

                this.isChatMinimized = true;

                if (window.LC_API?.chat_window_maximized?.()) {
                    this.isChatMinimized = false;
                    this.isVisible = false;
                }
            });
        },
        handleChatShow() {
            this.isChatMinimized = false;
            this.isVisible = false;
            this.hasNewMessage = false;
            this.isMainButtonHovered = false;
            this.isCloseButtonHovered = false;
        },
        handleChatHide() {
            this.isChatMinimized = true;
            this.isVisible = !webix.storage.local.get(ID_KEY_STORAGE_CHAT_MISSING);
            this.isMainButtonHovered = false;
            this.isCloseButtonHovered = false;
        },
        handleClick() {
            this.hasNewMessage = false;

            if (window.LiveChatWidget?.call) {
                try {
                    window.LiveChatWidget.call("maximize");
                } catch (e) {
                    if (window.LC_API?.open_chat_window) {
                        window.LC_API.open_chat_window();
                    } else {
                        app.trigger(appIds.events.ID_APP_EVENT_LIVECHAT_CLICK);
                    }
                    throw e;
                }
            } else if (window.LC_API?.open_chat_window) {
                window.LC_API.open_chat_window();
            } else {
                app.trigger(appIds.events.ID_APP_EVENT_LIVECHAT_CLICK);
            }
        },
        handleClose() {
            this.hideButton();
            app.trigger("app:notifyPopup:show", "support_hint");
        },
        hideButton() {
            webix.storage.local.put(ID_KEY_STORAGE_CHAT_MISSING, true);
            this.isVisible = false;
            this.isAnimating = false;
            this.animationStyle = {};
        },
        handleNewMessage() {
            if (this.isChatMinimized) {
                this.hasNewMessage = true;
                this.isVisible = true;
                this.isMainButtonHovered = false;
                this.isCloseButtonHovered = false;
                webix.storage.local.put(ID_KEY_STORAGE_CHAT_MISSING, false);
            }
        },
    },
};
</script>

<style module lang="less">
@import "./learningCenterSupportFloatingIcon.less";
</style>
